import {RootState} from 'store'
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {
  BrainFunctionConnectivityResponse,
  IndividualSummarySearchRequest,
  searchIndividualSummaryBrainFunctionConnectivityApi,
} from 'api/analysisApi'
import {openFailureModalAction} from 'features/modal/alertSlice'
import {getDefaultPeriodString, periodStringToDate} from 'helpers/dateHelper'

export const searchEegSummaryBrainFunctionConnectivity = createAsyncThunk(
  '/api/v1/analyze-follow-up/brain-function-connectivity',
  async (
    payload: IndividualSummarySearchRequest,
    {rejectWithValue, dispatch},
  ) => {
    try {
      const response =
        await searchIndividualSummaryBrainFunctionConnectivityApi(payload)
      return response.page
    } catch (err) {
      await dispatch(openFailureModalAction(err.message))
      return rejectWithValue(err.message)
    }
  },
)

interface EegSummaryBrainFunctionConnectivityState {
  loading: boolean
  error: any
  items: BrainFunctionConnectivityResponse[]
  period: PeriodString
  search: AnalysisSearchKind
  paging: Paging
  pagingInfo: {
    totalPages: number
    totalElements: number
  }
}

const initialState: EegSummaryBrainFunctionConnectivityState = {
  loading: false,
  error: null,
  items: [],
  period: getDefaultPeriodString(),
  search: {searchName: 'NAME', searchValue: ''},
  paging: {page: 0, size: 10},
  pagingInfo: {
    totalPages: 1,
    totalElements: 0,
  },
}

const slice = createSlice({
  name: 'eegSummaryBrainFunctionConnectivitySlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      searchEegSummaryBrainFunctionConnectivity.pending,
      (state, action) => {
        state.loading = true
        state.error = null

        const {period, search, paging} = action.meta.arg
        state.period = period
        state.search = search
        state.paging = paging
      },
    )
    builder.addCase(
      searchEegSummaryBrainFunctionConnectivity.fulfilled,
      (state, action) => {
        state.loading = false

        const {
          content: items,
          number: page,
          size,
          totalPages,
          totalElements,
        } = action.payload
        state.items = items
        state.loading = false
        state.error = null
        state.paging.page = page
        state.paging.size = size
        state.pagingInfo.totalPages = totalPages
        state.pagingInfo.totalElements = totalElements
      },
    )
    builder.addCase(
      searchEegSummaryBrainFunctionConnectivity.rejected,
      (state, action) => {
        state.loading = false
        state.error = action.payload
      },
    )
  },
})

export default slice.reducer

export const selectLoadingBrainFunctionConnectivity = (state: RootState) =>
  state.individualBrainFunctionConnectivity.loading
export const selectErrorBrainFunctionConnectivity = (state: RootState) =>
  state.individualBrainFunctionConnectivity.error
export const selectItemsBrainFunctionConnectivity = (state: RootState) =>
  state.individualBrainFunctionConnectivity.items
export const selectPeriodBrainFunctionConnectivity = (state: RootState) =>
  periodStringToDate(state.individualBrainFunctionConnectivity.period)
export const selectSearchBrainFunctionConnectivity = (state: RootState) =>
  state.individualBrainFunctionConnectivity.search
export const selectPagingBrainFunctionConnectivity = (state: RootState) =>
  state.individualBrainFunctionConnectivity.paging
export const selectPagingInfoBrainFunctionConnectivity = (state: RootState) =>
  state.individualBrainFunctionConnectivity.pagingInfo

export const selectQueryBrainFunctionConnectivity = (state: RootState) => ({
  period: state.individualBrainFunctionConnectivity.period,
  search: state.individualBrainFunctionConnectivity.search,
  paging: state.individualBrainFunctionConnectivity.paging,
})
